import { useCallback, useState } from 'react';

import { useAddFromLms } from '@/features/people/use-people';
import { apiErrorReason } from '@/lib/api';

import { proposalView, type ProposalView } from './code-proposal';
import type { OwnerSelection } from './components/OwnerField';
import { codeError, registrationError, titleError } from './presentation';
import { registerPayload } from './register-payload';
import type { DocumentOrigin, RegisterInput } from './types';
import { useCodeProposal } from './use-code-proposal';

// Estado del formulario del alta (G3-2/G3-3), compartido por "Nuevo documento" y por la
// aprobación de una solicitud: valores, código propuesto por el servidor, validación
// antes de enviar y ubicación de cada rechazo del API en su campo.

export interface DocumentFormValues {
  title: string;
  documentTypeId: string;
  processAreaId: string;
  origin: DocumentOrigin;
  sapModule: string;
  /** Código escrito: en un tipo libre, o al editar el propuesto. */
  code: string;
  editing: boolean;
  owner: OwnerSelection | null;
}

export const EMPTY_DOCUMENT_FORM: DocumentFormValues = {
  title: '',
  documentTypeId: '',
  processAreaId: '',
  origin: 'interno',
  sapModule: '',
  code: '',
  editing: false,
  owner: null,
};

const CODE_REASONS = new Set([
  'code_in_use',
  'code_retired',
  'code_pattern_mismatch',
  'code_required',
]);
// Cambiar tipo o proceso cambia el patrón: lo escrito y el módulo dejan de valer.
const RESET_CODE: Partial<DocumentFormValues> = { sapModule: '', code: '', editing: false };

export interface DocumentFormState {
  form: DocumentFormValues;
  view: ProposalView;
  isSap: boolean;
  /** Listo para enviar (falta validar el formato al enviar). */
  complete: boolean;
  codeMsg: string | null;
  titleMsg: string | null;
  setType: (id: string) => void;
  setArea: (id: string) => void;
  setSapModule: (code: string) => void;
  setCode: (code: string) => void;
  editCode: () => void;
  useProposed: () => void;
  setTitle: (title: string) => void;
  setOrigin: (origin: DocumentOrigin) => void;
  setOwner: (owner: OwnerSelection | null) => void;
  retryProposal: () => void;
  /** Marca los errores de formato; true si se puede enviar. */
  check: () => boolean;
  /** Ubica un rechazo en su campo; devuelve el mensaje general, o null si fue a un campo. */
  rejection: (err: unknown) => string | null;
  /** Cuerpo del alta, con el dueño ya resuelto (una persona del LMS se agrega antes). */
  payload: () => Promise<RegisterInput>;
  reset: (values: DocumentFormValues) => void;
}

export function useDocumentForm(initial: DocumentFormValues): DocumentFormState {
  const addFromLms = useAddFromLms();
  const [form, setForm] = useState<DocumentFormValues>(initial);
  const [checked, setChecked] = useState(false);
  const [codeTaken, setCodeTaken] = useState<string | null>(null);
  const [titleTaken, setTitleTaken] = useState<string | null>(null);
  const proposal = useCodeProposal({
    documentTypeId: form.documentTypeId,
    processAreaId: form.processAreaId,
    sapModule: form.sapModule,
  });
  const view = proposalView({
    ready: form.documentTypeId !== '' && form.processAreaId !== '',
    data: proposal.data,
    error: proposal.error,
    isLoading: proposal.isLoading,
  });

  const reset = useCallback((values: DocumentFormValues): void => {
    setForm(values);
    setChecked(false);
    setCodeTaken(null);
    setTitleTaken(null);
  }, []);

  const set = (patch: Partial<DocumentFormValues>): void => setForm((f) => ({ ...f, ...patch }));
  const manual = view.kind === 'blocked' && view.allowManual;
  const writesCode = view.kind === 'free' || manual || (view.kind === 'proposed' && form.editing);
  const isSap = view.kind === 'needs_sap' || (view.kind === 'proposed' && view.scheme === 'sap');
  const codeReady =
    view.kind === 'proposed' || ((view.kind === 'free' || manual) && form.code.trim() !== '');

  return {
    form,
    view,
    isSap,
    complete: form.title.trim() !== '' && codeReady,
    codeMsg: codeTaken ?? (checked && writesCode ? codeError(form.code) : null),
    titleMsg: titleTaken ?? (checked ? titleError(form.title) : null),
    setType: (documentTypeId) => set({ documentTypeId, ...RESET_CODE }),
    setArea: (processAreaId) => set({ processAreaId, ...RESET_CODE }),
    setSapModule: (sapModule) => set({ sapModule, code: '', editing: false }),
    setCode: (code) => {
      setCodeTaken(null);
      set({ code });
    },
    editCode: () => {
      if (view.kind === 'proposed') {
        set({ editing: true, code: view.code });
      }
    },
    useProposed: () => {
      setCodeTaken(null);
      set({ editing: false, code: '' });
    },
    setTitle: (title) => {
      setTitleTaken(null);
      set({ title });
    },
    setOrigin: (origin) => set({ origin }),
    setOwner: (owner) => set({ owner }),
    retryProposal: () => void proposal.refetch(),
    check: () => {
      setChecked(true);
      return (!writesCode || codeError(form.code) === null) && titleError(form.title) === null;
    },
    rejection: (err) => {
      const reason = apiErrorReason(err);
      if (reason !== undefined && CODE_REASONS.has(reason) && writesCode) {
        setCodeTaken(registrationError(err));
        return null;
      }
      if (reason === 'title_retired') {
        setTitleTaken(registrationError(err));
        return null;
      }
      return registrationError(err);
    },
    payload: async () => {
      const { owner } = form;
      const ownerUserId =
        owner === null
          ? undefined
          : owner.kind === 'qms'
            ? owner.userId
            : await addFromLms.mutateAsync({ authSubject: owner.authSubject });
      return registerPayload(form, { writesCode, isSap }, ownerUserId);
    },
    reset,
  };
}
